import type { Certification } from 'src/types/Certification';

export type CertKind = 'application' | 're-certification';

export interface CertKindLabelOptions {
  capitalize?: boolean;
  plural?: boolean;
}

const RECERT_PATTERN = /re-?cert|recert|renewal|interim|annual|biennial/i;

/**
 * Classifies a backend category name (e.g. `category.name` / `cert_type_label`)
 * as a re-certification when it matches a recert pattern, and application
 * otherwise.
 */
export function certKindFromLabel(label: string | null | undefined): CertKind {
  if (label && RECERT_PATTERN.test(label)) {
    return 're-certification';
  }
  return 'application';
}

/**
 * Determines the friendly cert kind from the certification's category name
 * (e.g. "Recertification", "Renewals", "Interim" -> re-certification;
 * everything else -> application).
 *
 * Note: `status.is_initial` is NOT used here — the backend does not reliably
 * send it, and where it does exist it denotes the first status in the workflow
 * rather than a move-in vs recert distinction.
 */
export function certKind(cert: Pick<Certification, 'category'>): CertKind {
  return certKindFromLabel(cert.category?.name);
}

export function certKindLabel(
  cert: Pick<Certification, 'category'>,
  { capitalize = false, plural = false }: CertKindLabelOptions = {}
): string {
  let label: string = certKind(cert);
  if (plural) {
    label = label === 'application' ? 'applications' : 're-certifications';
  }
  if (capitalize) {
    label = label.charAt(0).toUpperCase() + label.slice(1);
  }
  return label;
}

export function certKindForId(
  certifications: Certification[],
  certId: number | null | undefined
): CertKind | null {
  if (certId == null) {
    return null;
  }
  const cert = certifications.find((c) => c.id === certId);
  return cert ? certKind(cert) : null;
}

/**
 * Maps a backend type label (e.g. `cert_type_label`) to a capitalized display
 * label. Returns '' for an empty label so callers can render their own
 * fallback.
 */
export function certTypeDisplayLabel(label: string | null | undefined): string {
  if (!label) {
    return '';
  }
  return certKindFromLabel(label) === 're-certification'
    ? 'Re-certification'
    : 'Application';
}
